export const CARD_NUMBER_LENGTH = 16;
export const AMEX_CARD_NUMBER_LENGTH = 15;
export const CVC_LENGTH = 3;
export const AMEX_CVC_LENGTH = 4;

export const getCardDigits = (value) => String(value || "").replace(/\D/g, "");

// American Express cards start with 34 or 37
export const isAmexCard = (value) => /^3[47]/.test(getCardDigits(value));

// Amex is 15 digits, every other brand we accept is 16
export const getCardNumberLength = (value) =>
  isAmexCard(value) ? AMEX_CARD_NUMBER_LENGTH : CARD_NUMBER_LENGTH;

// Amex prints a 4 digit CID, the others a 3 digit CVC
export const getCvcLength = (value) => (isAmexCard(value) ? AMEX_CVC_LENGTH : CVC_LENGTH);

// "#### ###### #####" for Amex, "#### #### #### ####" for the rest
export const formatCardNumber = (value) => {
  const digits = getCardDigits(value).slice(0, getCardNumberLength(value));

  if (isAmexCard(digits)) {
    return [digits.slice(0, 4), digits.slice(4, 10), digits.slice(10)].filter(Boolean).join(" ");
  }

  return digits.replace(/(\d{4})(?=\d)/g, "$1 ");
};

export const formatCvc = (value, cardNumber) =>
  getCardDigits(value).slice(0, getCvcLength(cardNumber));

// Keep only digits and auto insert the slash: "1225" -> "12/25"
export const formatExpiration = (value) => {
  let digits = String(value || "").replace(/\D/g, "");

  // A leading digit above 1 can only be a single digit month (ex. "5" -> "05")
  if (digits.length === 1 && Number(digits) > 1) {
    digits = `0${digits}`;
  }

  digits = digits.slice(0, 4);

  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2)}`;
};

// "12/25" -> { month: 12, year: 2025 }, null when incomplete
export const parseExpiration = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length !== 4) return null;

  const month = Number(digits.slice(0, 2));
  const year = 2000 + Number(digits.slice(2));
  if (month < 1 || month > 12) return null;

  return { month, year };
};

export const validateExpiration = (value) => {
  const expiration = parseExpiration(value);
  if (!expiration) return false;

  const now = new Date();
  const currentMonth = now.getMonth() + 1;
  const currentYear = now.getFullYear();

  if (expiration.year < currentYear) return false;
  if (expiration.year === currentYear && expiration.month < currentMonth) return false;

  return true;
};

export const validateCardNumber = (number) => {
  const digits = getCardDigits(number);

  if (digits.length !== getCardNumberLength(digits)) {
    return false;
  }

  return luhnCheck(digits);
};

export const validateCvc = (value, cardNumber) =>
  getCardDigits(value).length === getCvcLength(cardNumber);

const luhnCheck = (val) => {
  let checksum = 0; // running checksum total
  let j = 1; // takes value of 1 or 2

  // Process each digit one by one starting from the last
  for (let i = val.length - 1; i >= 0; i--) {
    let calc = 0;
    // Extract the next digit and multiply by 1 or 2 on alternative digits.
    calc = Number(val.charAt(i)) * j;

    // If the result is in two digits add 1 to the checksum total
    if (calc > 9) {
      checksum = checksum + 1;
      calc = calc - 10;
    }

    // Add the units element to the checksum total
    checksum = checksum + calc;

    // Switch the value of j
    if (j == 1) {
      j = 2;
    } else {
      j = 1;
    }
  }

  //Check if it is divisible by 10 or not.
  return checksum % 10 == 0;
};
